import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { LoadingBar } from 'components/loading-bar/loading-bar';
import { labels } from 'locales/en.label';
import type React from 'react';
import type { FunctionComponent, ReactNode } from 'react';

export interface ModalProps {
	isShown: boolean;
	hide: () => void;
	headerText?: string;
	messageBody?: ReactNode | null;
	isConfirmationModal?: boolean;
	onConfirm?: (event?: React.MouseEvent<HTMLDivElement>) => void;
	onCancel?: () => void;
	confirmBtnText?: string;
	cancelBtnText?: string;
	hasIcon?: boolean;
	icon?: React.ReactNode;
	children?: React.ReactNode;
	loading?: boolean;
	className?: string;
	headerClassName?: string;
	confirmBtnClassName?: string;
}

export const Modal: FunctionComponent<ModalProps> = ({
	isShown,
	hide,
	headerText,
	messageBody,
	hasIcon,
	isConfirmationModal,
	confirmBtnText = labels.common.confirm,
	cancelBtnText = labels.common.cancel,
	onConfirm,
	onCancel,
	icon,
	children,
	loading = false,
	className,
	headerClassName,
	confirmBtnClassName,
}) => {
	const handleOpenChange = (open: boolean) => {
		if (!open) {
			hide();
		}
	};

	const handleCancelClick = onCancel ?? hide;

	const handleConfirmClick = () => {
		if (onConfirm) {
			onConfirm();
		}
	};

	return (
		<Dialog open={isShown} onOpenChange={handleOpenChange} modal={false}>
			<DialogOverlay className='tw-bg-gray-50/5' />
			<DialogContent
				className={cn('tw-max-w-xl', className)}
				onEscapeKeyDown={hide}
				onPointerDownOutside={hide}
			>
				<LoadingBar loading={loading}>
					<>
						{headerText && (
							<DialogHeader className='tw-mb-4 tw-border-b tw-border-red-500 tw-pb-4'>
								<div className='tw-flex tw-items-center tw-gap-2'>
									{hasIcon && icon && <div>{icon}</div>}
									<DialogTitle
										className={`${headerClassName} && ${headerClassName}`}
									>
										{headerText}
									</DialogTitle>
								</div>
							</DialogHeader>
						)}
						{/* Add DialogDescription for accessibility compliance */}
						<DialogDescription className='tw-sr-only'>
							{messageBody
								? typeof messageBody === 'string'
									? messageBody
									: 'Modal dialog content'
								: headerText || 'Modal dialog'}
						</DialogDescription>
						{(messageBody || children) && (
							<div className={headerText ? 'tw-mt-4' : ''}>
								{messageBody && <div className='tw-mb-4'>{messageBody}</div>}
								{children}
							</div>
						)}

						{isConfirmationModal && (
							<DialogFooter className='tw-mt-4'>
								<Button variant='outline' onClick={handleCancelClick}>
									{cancelBtnText}
								</Button>
								<Button
									className={`tw-text-white ${confirmBtnClassName} ? ${confirmBtnClassName} : tw-bg-green hover:tw-bg-greenHover`}
									variant='outline'
									onClick={handleConfirmClick}
								>
									{confirmBtnText}
								</Button>
							</DialogFooter>
						)}
					</>
				</LoadingBar>
			</DialogContent>
		</Dialog>
	);
};
